/*
  window.shintopCart — the one step every Shintop cart writer takes after its write lands
  (audit 2026-10-14, R04-1 / S04-002, Theme Audit Queue chunk 8).

  WHY ONE HELPER. Four Shintop controls write the cart — the card stepper, Frequently Bought
  Together, the sticky buy bar and the add-on — and each had grown its own idea of what to do
  next: three `cartUpdate` payload shapes, four ways of repainting the drawer, and a fifth file
  (`shintop-cart-drawer-sync.js`, now retired) patching the drawer's empty state up afterwards.
  Every fix round on the cart had to touch all of them at once, and each missed something:

    - FBT replaced `#CartDrawer` whole, which threw away the overlay and the click listener
      Dawn binds to it once — so the backdrop stopped closing the drawer — and looked for the
      bubble's markup by an id the section response does not carry (S03-004).
    - The stepper refreshed only the bubble, and Dawn's own refresh off the publish replaces
      `cart-drawer-items` and `.cart-drawer__footer` and nothing else. The empty block and the
      `is-empty` class are neither, so removing the last line from a card left a drawer with a
      header and nothing under it, and Check out live over an empty cart (S03-005).
    - The add-on never repainted the bubble at all (C-d).

  WHAT IT DOES, IN ORDER.
    1. Re-render `.drawer__inner` from the write's own `cart-drawer` section — in EVERY case,
       empty or not. `.drawer__inner` holds the empty block, the lines, the footer and Check out,
       and it is a child of `#CartDrawer`, so the overlay beside it (and Dawn's listener on it)
       is never touched. The drawer's `is-empty` comes from the same render: the server decided
       it, this file only copies it across, so the empty block is present exactly when the class
       is set and `open()`'s focus trap always finds what it reaches for.
    2. Repaint the header bubble from the write's `cart-icon-bubble` section.
    3. Open the drawer, only when the writer asked to, handing it the control that opened it so
       Dawn's `close()` gives focus back there (S06-016). The card stepper never asks.
    4. Publish ONE payload shape, `{ source, cartData, ...extra }`, where `cartData` is always
       the whole cart. `/cart/update.js` answers with the cart; `/cart/add.js` answers with the
       line(s) it added — `{ items: [...] }` for an items[] add, which LOOKS like a cart and is
       not one: a card stepper or the Clarity tagger reading it as one would see every other
       line at 0 — so for an add the cart is read once with `/cart.js`. Dawn's own
       `standard-actions-override.js` does the same for the same reason (`quick-add-bulk.js`
       reads `cartData.items` unconditionally).

  Steps 1–3 run before 4, so the shopper sees the drawer without waiting for that read. Each step
  is caught on its own: the write has landed, and a failed repaint must neither stop the publish
  nor show an error for an action that worked (audit 2026-09-13, S03-02).

  EACH WRITER KEEPS ITS OWN REQUEST, and asks this file which sections to bundle into it
  (`sections()`), so the render costs no extra round trip. When a response lacks the drawer
  section (Shopify sends `null` for a section that failed to render) the drawer is read with one
  GET instead, never left as it was.

  🔴 This is the only Shintop file that renders the drawer. A new Shintop control that writes the
  cart calls `afterCartWrite` and nothing else — never `renderContents`, never its own loop.

  ON /cart THE PAGE IS A THIRD SURFACE (audit 2026-10-14, S03-013, chunk 9). `sections()` adds
  the page's list and footer, `render()` repaints their `.js-contents` and copies `is-empty`
  from the same render — Dawn's own subscriber refreshes the lines after the publish and leaves
  both the subtotal and the class alone, so a "Complete your cart" add left the subtotal one
  behind, and an add after emptying the page in place left it saying "Your cart is empty" over
  the line just added. The drawer, the page's list, the footer and the bubble are four sections;
  five is Shopify's cap on one request.
*/
(() => {
  if (window.shintopCart) return;

  const DRAWER = 'cart-drawer';
  const BUBBLE = 'cart-icon-bubble';

  /*
    Publishers whose own refresh leaves the drawer's body, and the cart page's empty state,
    stale. `external-refresh` is `standard-actions-override.js`, after `Shopify.actions.updateCart`
    — which Shopify's WebMCP agent tools call on live (audit 2026-10-14, H04-01, chunk 2's read 3).
    It re-renders the bubble and the page footer's totals and leaves the rest to Dawn's
    subscribers, which replace the drawer's lines and the page's list and never touch `is-empty`:
    a drawer that loaded empty kept saying "Your cart is empty" over the line an agent had just
    added, with Check out dead, and on /cart the page did the same (chunk 9). Dawn's own writers
    re-render the drawer themselves (`product-form` through `renderContents`, the drawer's
    quantity controls through `.drawer__inner`), so they are not here — but see `cart-items`
    in `listen()`.
  */
  const LEFT_STALE_BY = new Set(['external-refresh']);

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  const isCart = (data) => Boolean(data) && Array.isArray(data.items) && typeof data.item_count === 'number';

  const cartUrl = () => (typeof routes !== 'undefined' && routes.cart_url) || '/cart';

  /*
    The cart page's list and footer, found by the element ids Dawn's `cart.js` renders them
    through; a response keys each by its section id (`data-id`). Null on every other page.
  */
  function cartPage() {
    const list = document.querySelector('cart-items');
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (!list || !items || !items.dataset.id) return null;
    return { list, items, footer: footer && footer.dataset.id ? footer : null };
  }

  const pageIds = (page) => [page.items.dataset.id].concat(page.footer ? [page.footer.dataset.id] : []);

  /* The section ids a writer bundles into its request — keyed as the response keys them. */
  function sections() {
    const ids = [];
    if (document.querySelector('cart-drawer')) {
      ids.push(DRAWER);
    } else {
      const notification = document.querySelector('cart-notification');
      if (notification && typeof notification.getSectionsToRender === 'function') {
        notification.getSectionsToRender().forEach((entry) => ids.push(entry.section || entry.id));
      }
    }
    const page = cartPage();
    if (page) pageIds(page).forEach((id) => ids.includes(id) || ids.push(id));
    if (!ids.includes(BUBBLE)) ids.push(BUBBLE);
    return ids;
  }

  async function drawerHtml(state) {
    const html = state && state.sections && state.sections[DRAWER];
    if (typeof html === 'string') return html;
    const response = await fetch(`${cartUrl()}?section_id=${DRAWER}`);
    return response.ok ? response.text() : null;
  }

  function renderDrawer(drawer, html) {
    const doc = parse(html);
    const fresh = doc.querySelector('.drawer__inner');
    const inner = drawer.querySelector('.drawer__inner');
    if (!fresh || !inner) return;
    inner.innerHTML = fresh.innerHTML;
    const freshDrawer = doc.querySelector('cart-drawer');
    const empty = freshDrawer ? freshDrawer.classList.contains('is-empty') : Boolean(fresh.querySelector('.drawer__inner-empty'));
    drawer.classList.toggle('is-empty', empty);
  }

  async function pageHtml(state, page) {
    const ids = pageIds(page);
    const bundled = state && state.sections;
    if (bundled && ids.every((id) => typeof bundled[id] === 'string')) return bundled;
    const response = await fetch(`${cartUrl()}?sections=${ids.join(',')}`);
    return response.ok ? response.json() : null;
  }

  /*
    The two `.js-contents` blocks Dawn's own page writer repaints, and the state that sits outside
    them, copied from the same render: `is-empty` on the list and on the footer (which decides
    whether the page says "Your cart is empty", and the two-column grid), and Check out's
    `disabled`, which is server-rendered on a page that loaded empty.
  */
  function renderPage(page, html) {
    const listHtml = html[page.items.dataset.id];
    if (typeof listHtml === 'string') {
      const doc = parse(listHtml);
      const fresh = doc.querySelector('#main-cart-items .js-contents');
      const target = page.items.querySelector('.js-contents');
      if (fresh && target) target.innerHTML = fresh.innerHTML;
      const freshList = doc.querySelector('cart-items');
      if (freshList) page.list.classList.toggle('is-empty', freshList.classList.contains('is-empty'));
    }
    const footerHtml = page.footer && html[page.footer.dataset.id];
    if (typeof footerHtml === 'string') {
      const doc = parse(footerHtml);
      const fresh = doc.querySelector('#main-cart-footer .js-contents');
      const target = page.footer.querySelector('.js-contents');
      if (fresh && target) target.innerHTML = fresh.innerHTML;
      const freshFooter = doc.getElementById('main-cart-footer');
      if (freshFooter) page.footer.classList.toggle('is-empty', freshFooter.classList.contains('is-empty'));
      const freshCheckout = doc.getElementById('checkout');
      const checkout = document.getElementById('checkout');
      if (freshCheckout && checkout) checkout.toggleAttribute('disabled', freshCheckout.hasAttribute('disabled'));
    }
  }

  function renderBubble(state) {
    const html = state && state.sections && state.sections[BUBBLE];
    const bubble = document.getElementById(BUBBLE);
    if (typeof html !== 'string' || !bubble) return;
    const source = parse(html).querySelector('.shopify-section');
    if (source) bubble.innerHTML = source.innerHTML;
  }

  async function render(state, { open, opener }) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      try {
        const html = await drawerHtml(state);
        if (html) renderDrawer(drawer, html);
      } catch (error) {
        console.warn('[shintop] could not re-render the cart drawer after a completed write:', error);
      }
    }
    const page = cartPage();
    if (page) {
      try {
        const html = await pageHtml(state, page);
        if (html) renderPage(page, html);
      } catch (error) {
        console.warn('[shintop] could not re-render the cart page after a completed write:', error);
      }
    }
    try {
      renderBubble(state);
    } catch (error) {
      console.warn('[shintop] could not repaint the cart bubble after a completed write:', error);
    }
    if (!open) return;
    try {
      if (drawer && typeof drawer.open === 'function') {
        drawer.open(opener || undefined);
        return;
      }
      // A notification renders from the single line /cart/add.js returns, as Dawn's product form does.
      const notification = document.querySelector('cart-notification');
      if (notification && typeof notification.renderContents === 'function') {
        if (state && state.key) notification.renderContents(state);
        return;
      }
      window.location.href = cartUrl();
    } catch (error) {
      console.warn('[shintop] could not open the cart after a completed write:', error);
    }
  }

  async function wholeCart(state) {
    if (isCart(state)) return state;
    try {
      const response = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } });
      const cart = response.ok ? await response.json() : null;
      return isCart(cart) ? cart : undefined;
    } catch {
      return undefined;
    }
  }

  async function afterCartWrite(state, { source, open = false, opener = null, ...extra } = {}) {
    await render(state, { open, opener });
    const cartData = await wholeCart(state);
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { ...extra, source, cartData });
      }
    } catch (error) {
      console.warn('[shintop] a cartUpdate subscriber failed after a completed write:', error);
    }
  }

  function refreshDrawer(drawer) {
    drawerHtml(null)
      .then((html) => html && renderDrawer(drawer, html))
      .catch((error) => console.warn('[shintop] could not re-render the cart drawer after an outside change:', error));
  }

  function refreshPage(page) {
    pageHtml(null, page)
      .then((html) => html && renderPage(page, html))
      .catch((error) => console.warn('[shintop] could not re-render the cart page after an outside change:', error));
  }

  /*
    ON /cart THE PAGE'S LIST AND THE DRAWER BOTH PUBLISH `source: 'cart-items'`, each re-renders
    only itself, and Dawn's subscribers on both ignore that source. So whichever one did NOT
    write is left stale (chunk 9):
      - the page's bin emptied the cart and the drawer kept its lines under `is-empty` with no
        empty block — opening it from the header gave `open()` a null focus container
        (`trapFocus(null)` threw) and a blank panel (S10-H01, stock Dawn);
      - the drawer's bin removed line 1 and the page kept showing it, so the page's line 1 was
        now a DIFFERENT product on the server, and its bin removed that one (S11-07).
    The open drawer is the one the shopper used — the page behind its overlay cannot be reached —
    so an open drawer means refresh the page, a closed one means refresh the drawer. Off /cart
    only the drawer publishes this, and it has rendered itself.
  */
  function listen() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event) return;
      const drawer = document.querySelector('cart-drawer');
      const page = cartPage();
      if (LEFT_STALE_BY.has(event.source)) {
        if (drawer) refreshDrawer(drawer);
        if (page) refreshPage(page);
        return;
      }
      if (event.source !== 'cart-items' || !page) return;
      if (drawer && drawer.classList.contains('active')) refreshPage(page);
      else if (drawer) refreshDrawer(drawer);
    });
  }

  window.shintopCart = { sections, afterCartWrite };

  if (typeof subscribe === 'function') listen();
  else document.addEventListener('DOMContentLoaded', listen, { once: true });
})();
